import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Divider,
  TextField, InputAdornment, IconButton, Menu, MenuItem, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";

const AUD = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v ?? 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Raw fetch helper for multipart file uploads (phappsRequest only does JSON)
async function uploadFile(endpoint, formData) {
  const token = localStorage.getItem("jwtToken");
  const urlBase = import.meta.env.VITE_APP_DATA_API_URL;
  const res = await fetch(`${urlBase}${endpoint}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Upload failed (${res.status})`);
  }
  return res.json();
}

const STRENGTH_COLOR = { strong: "#2e7d32", medium: "#e65100", weak: "#757575" };
const STRENGTH_BG    = { strong: "#e8f5e9", medium: "#fff3e0", weak: "#f5f5f5" };

function StrengthPill({ strength, hint }) {
  if (!strength) return null;
  return (
    <Tooltip title={hint || ""} placement="left">
      <Chip
        label={strength}
        size="small"
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          color: STRENGTH_COLOR[strength],
          bgcolor: STRENGTH_BG[strength],
          border: `1px solid ${STRENGTH_COLOR[strength]}40`,
          height: 20,
          ml: 1,
        }}
      />
    </Tooltip>
  );
}

function KebabMenu({ items }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((item) => (
          <MenuItem key={item.label} onClick={() => { setAnchor(null); item.action(); }}
            sx={{ fontSize: "0.85rem", color: item.danger ? "#c62828" : "inherit" }}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function StatCard({ label, count, value, color }) {
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: 2, px: 2.5, py: 1.5, minWidth: 160, borderTop: `3px solid ${color}` }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
        {count}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>{AUD(value)}</Typography>
    </Box>
  );
}

export default function ZephyrBankReconPage() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedTrans, setSelectedTrans] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [recentlyMatched, setRecentlyMatched] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [aliasPrompt, setAliasPrompt] = useState(null);
  const [editedAlias, setEditedAlias] = useState("");
  const [transSearch, setTransSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [sumRes, transRes, payRes, recentRes] = await Promise.all([
        phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_TRANSACTIONS, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_PAYMENTS, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_RECENTLY_MATCHED, "GET"),
      ]);
      setSummary(sumRes?.data ?? null);
      setTransactions(transRes?.data ?? []);
      setPayments(payRes?.data ?? []);
      setRecentlyMatched(recentRes?.data ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Left column: payments — filtered by search
  const displayPayments = payments.filter((p) =>
    !paymentSearch || (p.PayerName || "").toLowerCase().includes(paymentSearch.toLowerCase()) || (p.ReferenceNumber || "").toLowerCase().includes(paymentSearch.toLowerCase())
  );

  // Right column: when a payment is selected, highlight bank credits matching by amount
  const transHints = selectedPayment
    ? transactions.filter((t) => Math.abs(t.TransAmount - parseFloat(selectedPayment.remaining_amount ?? selectedPayment.TotalPaymentValue)) < 0.005)
    : null;

  const filteredTrans = transactions.filter((t) =>
    !transSearch || t.TransText?.toLowerCase().includes(transSearch.toLowerCase())
  );
  const displayTrans = transHints !== null ? transHints : filteredTrans;

  const handleSelectPayment = (payment) => {
    setSelectedTrans(null);
    setSuggestions(null);
    if (selectedPayment?.PaymentID === payment.PaymentID) {
      setSelectedPayment(null);
      return;
    }
    setSelectedPayment(payment);
  };

  const handleSelectTrans = (trans) => {
    setSelectedTrans((prev) =>
      prev?.TransID === trans.TransID ? null : trans
    );
  };

  const handleMatch = async () => {
    if (!selectedTrans || !selectedPayment) return;
    setMatching(true);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_RECON_MATCH, "POST", {
        transID: selectedTrans.TransID,
        paymentID: selectedPayment.PaymentID,
      });
      setSelectedTrans(null);
      setSelectedPayment(null);
      setSuggestions(null);
      if (res?.data?.suggestedAlias) {
        setAliasPrompt(res.data.suggestedAlias);
        setEditedAlias(res.data.suggestedAlias.aliasText);
      }
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setMatching(false);
    }
  };

  const handleSaveAlias = async (save) => {
    if (save && aliasPrompt) {
      try {
        await phappsRequest(API_PATHS.ZEPHYR_RECON_ALIAS, "POST", {
          payerType: aliasPrompt.payerType,
          payerID: aliasPrompt.payerID,
          aliasText: editedAlias,
        });
      } catch {
        // non-fatal
      }
    }
    setAliasPrompt(null);
    setEditedAlias("");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setBatchResult(null);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(API_PATHS.ZEPHYR_RECON_UPLOAD, fd);
      setBatchResult(res?.data ?? null);
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleAutoMatch = async () => {
    setAutoRunning(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_AUTO_MATCH, "POST");
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setAutoRunning(false);
    }
  };

  const handleIgnoreTrans = async (transID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORE_TRANS, "POST", { transID });
      setTransactions((prev) => prev.filter((t) => t.TransID !== transID));
      if (selectedTrans?.TransID === transID) { setSelectedTrans(null); setSuggestions(null); }
      await phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET").then((r) => setSummary(r?.data));
    } catch (e) { setError(e.message); }
  };

  const handleIgnorePayment = async (paymentID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORE_PAYMENT, "POST", { paymentID });
      setPayments((prev) => prev.filter((p) => p.PaymentID !== paymentID));
      setSuggestions((prev) => prev ? prev.filter((p) => p.PaymentID !== paymentID) : null);
      if (selectedPayment?.PaymentID === paymentID) setSelectedPayment(null);
      await phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET").then((r) => setSummary(r?.data));
    } catch (e) { setError(e.message); }
  };

  const handleUnmatch = async (transID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_UNMATCH, "POST", { transID });
      await loadAll();
    } catch (e) { setError(e.message); }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 4 }}>
        <CircularProgress sx={{ color: Z_PURPLE }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#f4f5f7" }}>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: Z_PURPLE, px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem", flex: 1 }}>
          Bank Reconciliation
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, fontSize: "0.8rem" }}
        >
          {uploading ? "Uploading…" : "Import CSV"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={autoRunning ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
          disabled={autoRunning}
          onClick={handleAutoMatch}
          sx={{ color: Z_BLUE, borderColor: `${Z_BLUE}80`, "&:hover": { borderColor: Z_BLUE, bgcolor: `${Z_BLUE}15` }, fontSize: "0.8rem" }}
        >
          {autoRunning ? "Running…" : "Run Auto-Match"}
        </Button>
      </Box>

      {/* ── Summary strip ────────────────────────────────────────────── */}
      <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 2, flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
        <StatCard label="Unmatched Bank Credits" count={summary?.unmatchedTransactions ?? 0} value={summary?.unmatchedTransactionsValue} color={Z_BLUE} />
        <StatCard label="Unmatched Payments" count={summary?.unmatchedPayments ?? 0} value={summary?.unmatchedPaymentsValue} color={Z_PURPLE} />
        {summary?.lastBatch && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, px: 2, py: 1.5, fontSize: "0.8rem", color: "#555" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Last Import</Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "#333", fontWeight: 600 }}>{summary.lastBatch.Filename}</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
              {fmtDate(summary.lastBatch.UploadedAt)} &middot; {summary.lastBatch.RowsInserted} rows &middot; {summary.lastBatch.AutoMatched} auto-matched
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Batch result ─────────────────────────────────────────────── */}
      {batchResult && (
        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Alert severity="success" onClose={() => setBatchResult(null)} sx={{ fontSize: "0.82rem" }}>
            Import complete — {batchResult.RowsInserted} inserted, {batchResult.RowsSkipped} skipped (duplicates), {batchResult.AutoMatched} auto-matched.
            {batchResult.ParseErrors > 0 && ` (${batchResult.ParseErrors} lines could not be parsed)`}
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Box>
      )}

      {/* ── Two-column main area ─────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", gap: 2, px: 3, pb: 2, overflow: "hidden", minHeight: 0 }}>

        {/* ── Left: payments (primary selection) ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE, mb: 1 }}>
              Unmatched Payments ({displayPayments.length})
            </Typography>
            <TextField
              size="small" placeholder="Search payments…" fullWidth
              value={paymentSearch} onChange={(e) => setPaymentSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#aaa" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.82rem" } }}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {displayPayments.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
                No unmatched payments
              </Box>
            ) : displayPayments.map((p) => {
              const isSelected = selectedPayment?.PaymentID === p.PaymentID;
              const remaining = parseFloat(p.remaining_amount ?? p.TotalPaymentValue);
              const isPartial = parseFloat(p.matched_total) > 0;
              return (
                <Box
                  key={p.PaymentID}
                  onClick={() => handleSelectPayment(p)}
                  sx={{
                    display: "flex", alignItems: "center", px: 2, py: 1.25,
                    cursor: "pointer", borderBottom: "1px solid #f5f5f5",
                    bgcolor: isSelected ? `${Z_PURPLE}10` : "transparent",
                    borderLeft: isSelected ? `3px solid ${Z_PURPLE}` : "3px solid transparent",
                    "&:hover": { bgcolor: isSelected ? `${Z_PURPLE}18` : "#fafafa" },
                    transition: "background 0.1s",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 600 }}>
                      {p.PayerName || <span style={{ color: "#aaa", fontStyle: "italic" }}>No payer linked</span>}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.25 }}>
                      {fmtDate(p.PayDate)}
                      {p.ReferenceNumber ? ` · ${p.ReferenceNumber}` : ""}
                      {isPartial && ` · ${AUD(parseFloat(p.matched_total))} matched`}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a" }}>
                      {AUD(remaining)}
                    </Typography>
                    {isPartial && (
                      <Typography sx={{ fontSize: "0.7rem", color: "#888" }}>
                        of {AUD(p.TotalPaymentValue)}
                      </Typography>
                    )}
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 0.5 }}>
                    <KebabMenu items={[
                      { label: "Ignore for reconciliation", danger: true, action: () => handleIgnorePayment(p.PaymentID) },
                    ]} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Right: bank credits (secondary selection) ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE, mb: 1 }}>
              {transHints !== null
                ? `Matching Bank Credits for ${AUD(parseFloat(selectedPayment?.remaining_amount ?? selectedPayment?.TotalPaymentValue))} (${transHints.length})`
                : `Bank Credits (${filteredTrans.length})`}
            </Typography>
            <TextField
              size="small" placeholder="Search transactions…" fullWidth
              value={transSearch} onChange={(e) => setTransSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#aaa" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.82rem" } }}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {displayTrans.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
                {transHints !== null ? "No bank credits match this amount" : "No unmatched bank transactions"}
              </Box>
            ) : displayTrans.map((t) => {
              const isSelected = selectedTrans?.TransID === t.TransID;
              const isHint = transHints !== null;
              return (
                <Box
                  key={t.TransID}
                  onClick={() => handleSelectTrans(t)}
                  sx={{
                    display: "flex", alignItems: "center", px: 2, py: 1.25,
                    cursor: "pointer", borderBottom: "1px solid #f5f5f5",
                    bgcolor: isSelected ? `${Z_BLUE}12` : isHint ? `${Z_BLUE}06` : "transparent",
                    borderLeft: isSelected ? `3px solid ${Z_BLUE}` : "3px solid transparent",
                    "&:hover": { bgcolor: isSelected ? `${Z_BLUE}18` : "#fafafa" },
                    transition: "background 0.1s",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(t.TransText ?? "").replace(/^transfer from\s*/i, "")}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.25 }}>
                      {fmtDate(t.TransDate)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a", ml: 1, flexShrink: 0 }}>
                    {AUD(t.TransAmount)}
                  </Typography>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 0.5 }}>
                    <KebabMenu items={[
                      { label: "Ignore this transaction", danger: true, action: () => handleIgnoreTrans(t.TransID) },
                    ]} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Match bar ────────────────────────────────────────────────── */}
      {selectedTrans && selectedPayment && (
        <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <Box sx={{ bgcolor: Z_PURPLE, borderRadius: 2, px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ color: Z_BLUE, fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontSize: "0.85rem", flex: 1 }}>
              Match Payment #{selectedPayment.PaymentID} — <strong>{selectedPayment.PayerName}</strong>{" "}
              {AUD(parseFloat(selectedPayment.remaining_amount ?? selectedPayment.TotalPaymentValue))} ({fmtDate(selectedPayment.PayDate)})
              {" → "}
              <strong>{(selectedTrans.TransText ?? "").replace(/^transfer from\s*/i, "")}</strong>{" "}
              {AUD(selectedTrans.TransAmount)} on {fmtDate(selectedTrans.TransDate)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={matching}
              onClick={handleMatch}
              sx={{ bgcolor: Z_BLUE, "&:hover": { bgcolor: "#1a9dc7" }, fontWeight: 700, flexShrink: 0 }}
            >
              {matching ? <CircularProgress size={14} color="inherit" /> : "Confirm Match"}
            </Button>
            <Button size="small" onClick={() => { setSelectedTrans(null); setSelectedPayment(null); setSuggestions(null); }}
              sx={{ color: "rgba(255,255,255,0.6)", minWidth: 0 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Recently matched ─────────────────────────────────────────── */}
      <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
        <Box
          onClick={() => setShowRecent((v) => !v)}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
        >
          {showRecent ? <ExpandLessIcon sx={{ fontSize: 18, color: "#888" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#888" }} />}
          <Typography sx={{ fontSize: "0.82rem", color: "#666", fontWeight: 600 }}>
            Recently matched ({recentlyMatched.length})
          </Typography>
        </Box>
        <Collapse in={showRecent}>
          <Box sx={{ mt: 1, bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", maxHeight: 260, overflowY: "auto" }}>
            {recentlyMatched.length === 0 ? (
              <Box sx={{ p: 2, color: "#aaa", fontSize: "0.82rem", textAlign: "center" }}>No recent matches</Box>
            ) : recentlyMatched.map((m) => (
              <Box key={m.AuditID} sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid #f5f5f5", "&:last-child": { borderBottom: "none" } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#333" }}>
                      {AUD(m.TransAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                      {m.TransText}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
                    {fmtDate(m.TransDate)} → {m.PayerName} #{m.PaymentID} · {m.MatchTier || m.Action} · {fmtDate(m.MatchedAt)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => handleUnmatch(m.TransID)}
                  sx={{ fontSize: "0.72rem", color: "#c62828", ml: 1, flexShrink: 0 }}
                >
                  Unmatch
                </Button>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* ── Alias prompt dialog ───────────────────────────────────────── */}
      <Dialog open={Boolean(aliasPrompt)} onClose={() => handleSaveAlias(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, pb: 1 }}>
          Save match hint?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: "#555", mb: 2 }}>
            Remember this text as a match hint for <strong>{aliasPrompt?.payerName}</strong>?
            Future transactions containing this text will auto-match.
          </Typography>
          <TextField
            fullWidth size="small" label="Alias text"
            value={editedAlias}
            onChange={(e) => setEditedAlias(e.target.value.toUpperCase())}
            inputProps={{ style: { fontFamily: "monospace", textTransform: "uppercase" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleSaveAlias(false)} sx={{ color: "#888", fontSize: "0.82rem" }}>No</Button>
          <Button
            onClick={() => handleSaveAlias(true)}
            disabled={!editedAlias.trim()}
            variant="contained"
            sx={{ bgcolor: Z_PURPLE, "&:hover": { bgcolor: "#2d1040" }, fontSize: "0.82rem" }}
          >
            Save "{editedAlias}"
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
